<script lang="ts">
	import RouteFeaturesStructure from '$lib/components/city/features/route/RouteFeaturesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_TYPE_COLOURS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import {
		SCENIC_STROLL_CATEGORY_LABELS,
		SCENIC_STROLL_META_DESCRIPTIONS
	} from '$lib/features/scenicStroll/scenicStrollCategory.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import { getTopFrequentValues, listFormatByAnd } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.SCENIC_STROLL;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const meta = $derived.by(() => {
		const firstAvailableImage: CoverImage | undefined = output.items.flatMap((ride) => ride.stops.flatMap((s) => s.images)).at(0);
		const itemsLength = output.items.length;
		const trailsWording = itemsLength === 1 ? 'trail' : 'trails';
		const uniqueCategories = [...new Set(output.items.flatMap((item) => item.categories))].filter(
			(category) => SCENIC_STROLL_META_DESCRIPTIONS[category]
		);
		const categoryPhrase =
			uniqueCategories.length >= 2
				? `from ${SCENIC_STROLL_META_DESCRIPTIONS[uniqueCategories[0]]} to ${SCENIC_STROLL_META_DESCRIPTIONS[uniqueCategories[1]]}`
				: uniqueCategories.length === 1
					? `featuring ${SCENIC_STROLL_META_DESCRIPTIONS[uniqueCategories[0]]}`
					: 'with scenic routes across the city';
		const featuredNames =
			itemsLength === 1
				? output.items[0].feature.englishName
				: `including the ${listFormatByAnd(output.items.slice(0, 2).map((item) => item.feature.englishName))}`;
		const topCategories = getTopFrequentValues(
			output.items.map((i) => i.categories),
			2
		).map((e) => SCENIC_STROLL_CATEGORY_LABELS[e]);
		return {
			title: `${location.city.label} Walking Routes: From ${topCategories.join(' to ')} Trails`,
			description: `Discover the best walking routes in ${location.city.label} with ${itemsLength} scenic ${trailsWording}, ${featuredNames}, ${categoryPhrase}.`,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const combinedJsonLd = $derived(data.output.jsonLdBreadcrumbs);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<RouteFeaturesStructure items={output.items} {location} language={output.countryLanguage} keepExploring={output.keepExploring}>
	{#snippet HeaderSnippet()}
		<HeroNeutral
			title={`Best Walking Routes in ${location.city.label}`}
			description={`Slow down and soak it all in. We’ve curated ${output.items.length} scenic walks that guide you through the city's most atmospheric streets, quietest parks, and finest views.`}
			breadcrumbs={data.output.breadcrumbs}
			decorations={{
				hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
			}}
		/>
	{/snippet}
</RouteFeaturesStructure>
